import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { isOnTrial, daysRemainingInTrial } from '../constants/tiers';
import { trackTrialExpired } from '../lib/analytics';

/**
 * Mundalo - Trial countdown strip
 * Golden Hour #E8A830 background. Sticks to top of every portal page when role === free_trial.
 * Pulses faintly in the last 3 days as gentle urgency.
 */
export default function TrialCountdownStrip({ user }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const onTrial = isOnTrial(user);
  const days = onTrial ? daysRemainingInTrial(user) : null;

  // GA4 - fire trial_expired exactly once per user-session when the
  // countdown hits zero. sessionStorage flag prevents refresh double-count.
  useEffect(() => {
    if (onTrial && days !== null && days <= 0 && typeof window !== 'undefined') {
      const key = `mundalo_trial_expired_fired_${user?.id || user?.email || 'anon'}`;
      if (!window.sessionStorage.getItem(key)) {
        trackTrialExpired();
        window.sessionStorage.setItem(key, '1');
      }
    }
  }, [onTrial, days, user?.id, user?.email]);

  if (!onTrial) return null;
  if (days === null) return null;
  // Iter 50 change: show the banner for the ENTIRE trial window (not just the
  // last 3 days). Display urgency styling only when ≤3 days remain.
  const isUrgent = days <= 3;

  return (
    <div
      data-testid="trial-countdown-strip"
      className={`w-full ${isUrgent ? 'bg-[#E8A830]' : 'bg-[#F4ECD8] border-b border-[#EFE6CF]'} text-[#0A1F2E] py-2.5 px-4 flex items-center justify-center gap-3 text-sm ${isUrgent ? 'animate-pulse-slow' : ''}`}
      style={{ animationDuration: '2s' }}
    >
      <Clock className="w-4 h-4 shrink-0" />
      <p className="font-medium">
        {days > 0 ? (
          <>
            Your trial ends in{' '}
            <span className="font-display italic text-base" data-testid="trial-days-remaining">{days}</span>{' '}
            day{days === 1 ? '' : 's'}
          </>
        ) : (
          <>Your free trial ends today</>
        )}
        <span className="hidden md:inline text-[#0A1F2E]/70 ml-2">- unlock everything before it locks</span>
      </p>
      <Link
        to="/portal/upgrade"
        data-testid="trial-countdown-cta"
        className="ml-2 underline underline-offset-4 font-semibold whitespace-nowrap hover:no-underline"
      >
        Upgrade →
      </Link>
    </div>
  );
}
